import type { Content } from "@/app/(site)/_components/ContentMapper"
import type { VisibilityAwareItem } from "@/src/utils/content/isContentVisible"

export type LayoutWidthDesktop = "two_columns" | "full_width"
export type HorizontalAlignmentDesktop = "right" | "center" | "left"

export type GridContentBlock = {
    _key: string
    _type: "gridContentBlock"
    internalTitle?: string
    page: number
    layoutWidthDesktop: LayoutWidthDesktop
    horizontalAlignmentDesktop: HorizontalAlignmentDesktop
    positionRowMobile: number
    positionRowDesktop: number
    content: Content[]
} & VisibilityAwareItem

// --- Row group types for desktop rendering ---

export type StandardRowGroup<T> = {
    type: "standard"
    products: T[]
    startIndex: number
}

export type ModuleRowGroup<T> = {
    type: "module"
    products: T[]
    module: GridContentBlock
    startIndex: number
}

export type FullWidthRowGroup = {
    type: "fullWidth"
    modules: GridContentBlock[]
    row: number
}

export type RowGroup<T> = StandardRowGroup<T> | ModuleRowGroup<T> | FullWidthRowGroup

// --- Default constants (used as fallbacks) ---

const DEFAULT_DESKTOP_COLS = 4
const DEFAULT_MOBILE_COLS = 2

// --- Helpers ---

export const isRowOne = (value: number) => value === 1

export function getGridContentForPage(
    gridContent: GridContentBlock[],
    page: number
): GridContentBlock[] {
    return gridContent.filter(block => block.page === page)
}

const setIfAbsent = <K, V>(map: Map<K, V>, key: K, value: V) => {
    if (!map.has(key)) {
        map.set(key, value)
    }
}

const pushToMapArray = <K, V>(map: Map<K, V[]>, key: K, value: V) => {
    const existing = map.get(key)
    if (existing) {
        existing.push(value)
        return
    }
    map.set(key, [value])
}

const buildModulesByRowMaps = (
    gridContent: GridContentBlock[],
    getRowPosition: (block: GridContentBlock) => number
) => {
    const twoColModulesByRow = new Map<number, GridContentBlock>()
    const fullWidthModulesByRow = new Map<number, GridContentBlock[]>()

    gridContent.forEach(block => {
        const rowNum = getRowPosition(block)
        if (!Number.isFinite(rowNum)) return

        if (block.layoutWidthDesktop === "two_columns") {
            setIfAbsent(twoColModulesByRow, rowNum, block)
            return
        }

        if (block.layoutWidthDesktop === "full_width") {
            pushToMapArray(fullWidthModulesByRow, rowNum, block)
        }
    })

    return { twoColModulesByRow, fullWidthModulesByRow }
}

// --- Grouping step ---

type NextStep<T> = {
    group: RowGroup<T>
    nextProductIndex: number
    nextRow: number
}

const getNextStep = <T>({
    currentRow,
    fullWidthModulesByRow,
    productIndex,
    products,
    twoColModulesByRow,
    productsPerRow
}: {
    currentRow: number
    fullWidthModulesByRow: Map<number, GridContentBlock[]>
    productIndex: number
    products: T[]
    twoColModulesByRow: Map<number, GridContentBlock>
    productsPerRow: number
}): NextStep<T> => {
    const fullWidthModules = fullWidthModulesByRow.get(currentRow)
    if (fullWidthModules && fullWidthModules.length > 0) {
        return {
            group: { type: "fullWidth", modules: fullWidthModules, row: currentRow },
            nextProductIndex: productIndex,
            nextRow: currentRow + 1
        }
    }

    const twoColModule = twoColModulesByRow.get(currentRow)
    if (twoColModule) {
        const productsInModuleGroup = Math.max(0, productsPerRow - 2)
        return {
            group: {
                type: "module",
                products: products.slice(
                    productIndex,
                    productIndex + productsInModuleGroup
                ),
                module: twoColModule,
                startIndex: productIndex
            },
            nextProductIndex: productIndex + productsInModuleGroup,
            nextRow: currentRow + 1
        }
    }

    return {
        group: {
            type: "standard",
            products: products.slice(productIndex, productIndex + productsPerRow),
            startIndex: productIndex
        },
        nextProductIndex: productIndex + productsPerRow,
        nextRow: currentRow + 1
    }
}

// --- Main grouping ---

type GroupingOptions = {
    productsPerRow?: number
    isMobile?: boolean
}

const hasValidPosition = (value: number) => Number.isFinite(value) && value > 0

export const groupProductsWithModules = <T>(
    products: T[],
    gridContent: GridContentBlock[],
    options?: GroupingOptions
): RowGroup<T>[] => {
    const productsPerRow = options?.productsPerRow ?? DEFAULT_DESKTOP_COLS
    const isMobile = options?.isMobile ?? false
    const getRowPosition = isMobile
        ? (block: GridContentBlock) => block.positionRowMobile
        : (block: GridContentBlock) => block.positionRowDesktop

    const filteredContent = gridContent.filter(block =>
        hasValidPosition(getRowPosition(block))
    )

    const { twoColModulesByRow, fullWidthModulesByRow } = buildModulesByRowMaps(
        filteredContent,
        getRowPosition
    )

    // Highest row number that has a module, so we keep iterating even after products run out.
    const maxModuleRow = Math.max(
        0,
        ...[...twoColModulesByRow.keys()],
        ...[...fullWidthModulesByRow.keys()]
    )

    const groups: RowGroup<T>[] = []

    for (
        let productIndex = 0, currentRow = 1;
        productIndex < products.length || currentRow <= maxModuleRow;
    ) {
        // After all products are consumed, skip empty rows but keep iterating
        // so modules assigned to later rows are still included in the output.
        if (productIndex >= products.length) {
            const hasModule =
                fullWidthModulesByRow.has(currentRow) ||
                twoColModulesByRow.has(currentRow)
            if (!hasModule) {
                currentRow++
                continue
            }
        }

        const { group, nextProductIndex, nextRow } = getNextStep({
            currentRow,
            fullWidthModulesByRow,
            productIndex,
            products,
            twoColModulesByRow,
            productsPerRow
        })
        groups.push(group)
        productIndex = nextProductIndex
        currentRow = nextRow
    }

    return groups
}

// --- Column placement ---

export const getModuleStartColumn = (
    alignment: HorizontalAlignmentDesktop,
    totalColumns: number = DEFAULT_DESKTOP_COLS
): number => {
    switch (alignment) {
        case "left":
            return 1
        case "center":
            return Math.max(1, Math.ceil((totalColumns - 1) / 2))
        case "right":
            return Math.max(1, totalColumns - 1)
        default:
            return Math.max(1, Math.ceil((totalColumns - 1) / 2))
    }
}

export const getRemainingColumns = (
    moduleStartColumn: number,
    totalColumns: number = DEFAULT_DESKTOP_COLS
): number[] => {
    const occupied = new Set([moduleStartColumn, moduleStartColumn + 1])
    return Array.from({ length: totalColumns }, (_, i) => i + 1).filter(
        c => !occupied.has(c)
    )
}

// --- Loading optimization ---

const getLoadingProps = (index: number, eagerCount: number) => {
    if (index < eagerCount) {
        return { isAboveTheFold: true }
    }
    return { isAboveTheFold: false }
}

export const getDesktopLoadingProps = (index: number, eagerCount = 8) =>
    getLoadingProps(index, eagerCount)

export const getMobileLoadingProps = (index: number, eagerCount = 4) =>
    getLoadingProps(index, eagerCount)

export const getEagerCounts = (
    gridContent: GridContentBlock[],
    desktopColumns: number = DEFAULT_DESKTOP_COLS,
    mobileColumns: number = DEFAULT_MOBILE_COLS
) => {
    const hasDesktopFirstRowFullWidthModule = gridContent.some(
        m => m.layoutWidthDesktop === "full_width" && isRowOne(m.positionRowDesktop)
    )
    const hasDesktopFirstRowTwoColModule = gridContent.some(
        m => m.layoutWidthDesktop === "two_columns" && isRowOne(m.positionRowDesktop)
    )

    const desktopEagerCount = (() => {
        if (hasDesktopFirstRowFullWidthModule) return 0
        if (hasDesktopFirstRowTwoColModule) return Math.max(0, desktopColumns - 2)
        return desktopColumns * 2
    })()

    const hasMobileFirstRowModule = gridContent.some(m => isRowOne(m.positionRowMobile))
    const mobileEagerCount = hasMobileFirstRowModule ? 0 : mobileColumns * 2

    return { desktopEagerCount, mobileEagerCount }
}
